import type { TestDataProps } from "@repo/ui";
import { useTranslation } from "react-i18next";
import type { FeeSettingsResponse, CurrencyPrimitive } from "@/api/cashier";
import { ProgressSpinner } from "@/components/progress-spinner/ProgressSpinner";
import { useCountdownRefetch } from "@/hooks/deposit/useCountdownRefetch";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import style from "./Summarize.module.css";
import { useFormatDate } from "@/hooks/useFormatDate";
import { ExchangeRate } from "@/components/ExchangeRate";
import { moneyToMajorUnits } from "@/utils/helpers";

type SummarizeProps = {
  accountCurrency?: CurrencyPrimitive;
  selectedProcessingCurrency?: CurrencyPrimitive;
  tradingValue: number;
  rate: number;
  showRate: boolean;
  showTotal: boolean;
  enabledRefetch?: boolean;
  fee?: FeeSettingsResponse;
  refetchRate: () => void;
  totalAmountCurrency?: CurrencyPrimitive;
  refetchIntervalSeconds?: number;
  showLastUpdated?: boolean;
} & TestDataProps;

const REFETCH_INTERVAL_SECONDS = 30;

export const Summarize = ({
  accountCurrency,
  selectedProcessingCurrency,
  tradingValue,
  rate,
  showRate,
  showTotal,
  fee,
  enabledRefetch = true,
  refetchRate,
  totalAmountCurrency,
  refetchIntervalSeconds = REFETCH_INTERVAL_SECONDS,
  showLastUpdated,
  "data-testid": testId,
  ...testProps
}: SummarizeProps) => {
  const { progress, refetchTimestamp } = useCountdownRefetch(
    refetchIntervalSeconds,
    refetchRate,
    enabledRefetch
  );
  const { t } = useTranslation();

  const { formatDate } = useFormatDate();
  const { formatAmount, formatFee } = useFormatAmount();

  // Safe parsing helpers
  const parseNumber = (value?: string | number): number => {
    if (value === undefined || value === null) {
      return 0;
    }
    const stringValue = String(value);
    const normalized = stringValue.replace(/[^0-9.-]/g, "");
    const num = Number.parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  };

  const calculateTotal = (
    trading: number,
    fee?: FeeSettingsResponse
  ): number => {
    if (!fee) return trading;
    const tradingValueAsNumber = Number(trading);
    if (fee.type === "percentage") {
      const percentageValue =
        typeof fee.value === "number"
          ? fee.value
          : (moneyToMajorUnits(fee.value) ?? Number.NaN);

      if (!Number.isFinite(percentageValue)) {
        return tradingValueAsNumber;
      }

      return (
        tradingValueAsNumber + (tradingValueAsNumber * percentageValue) / 100
      );
    }

    const feeValueAsNumber = moneyToMajorUnits(fee.value) ?? 0;
    return tradingValueAsNumber + feeValueAsNumber;
  };

  const tradingValueNum = parseNumber(tradingValue);
  const totalValue = calculateTotal(tradingValueNum, fee);

  return (
    <div data-testid={testId} className={style.conversion} {...testProps}>
      {showRate && selectedProcessingCurrency && accountCurrency && (
        <div className={`${style.row} ${style.rate}`}>
          <div>{t("deposit.methods.enter_amount.rate")}</div>
          <div
            className={style["row-text"]}
            data-testid={`${testId}-exchange-rate-value`}
          >
            <ExchangeRate
              unit={1}
              currency={selectedProcessingCurrency}
              exchangeRate={rate}
              exchangeCurrency={accountCurrency}
            />
            {!showLastUpdated && <ProgressSpinner progress={progress} />}
          </div>
        </div>
      )}

      {fee && selectedProcessingCurrency && (
        <div className={style.row}>
          <div>{t("deposit.methods.enter_amount.transaction_fee")}</div>
          <div>{formatFee(fee, selectedProcessingCurrency)}</div>
        </div>
      )}

      {showTotal && selectedProcessingCurrency && (
        <div className={`${style.row} ${style.sum}`}>
          <div data-testid="transaction-amount-label">
            {t("deposit.methods.enter_amount.transaction_amount")}
          </div>
          <div data-testid="transaction-amount-value">
            {formatAmount(
              totalValue,
              totalAmountCurrency ?? selectedProcessingCurrency
            )}
          </div>
        </div>
      )}

      {showLastUpdated && (
        <div className={style.lastUpdated}>
          <p
            className={style.lastUpdatedText}
            data-testid={`${testId}-last-updated`}
          >
            {t("common.last_updated", {
              timestamp: formatDate(refetchTimestamp),
            })}
          </p>
          <ProgressSpinner
            progress={progress}
            title={t("transfer.refreshing_amount_countdown")}
          />
        </div>
      )}
    </div>
  );
};
